import axios from 'axios';
import mockAPI from './mockAPI';

// Check if we should use mock API (for development)
// Set to false to use Laravel backend
const USE_MOCK_API = false; // process.env.REACT_APP_USE_MOCK_API === 'true';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Create a separate instance for file downloads with longer timeout
const apiFileDownload = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for file downloads
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for file download instance
apiFileDownload.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for file download instance
apiFileDownload.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isProtectedRoute = currentPath.startsWith('/app');
      
      localStorage.removeItem('token');
      
      if (isProtectedRoute) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No token found in localStorage for request:', config.url);
    }

    // For FormData, let axios/browser set Content-Type automatically with boundary
    // Don't manually set it as it needs the boundary parameter
    if (config.data instanceof FormData) {
      // Axios will automatically set Content-Type with boundary for FormData
      // Just make sure we don't override it with application/json
      if (config.headers['Content-Type'] === 'application/json') {
        delete config.headers['Content-Type'];
      }
      // Ensure Authorization header is preserved
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're on a protected route (/app/*)
      const currentPath = window.location.pathname;
      const isProtectedRoute = currentPath.startsWith('/app');
      
      localStorage.removeItem('token');
      
      // Only redirect if on a protected route
      if (isProtectedRoute) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
};

// Auth API
export const authAPI = {
  login: (credentials) => {
    // Ensure username field is used (Laravel backend expects 'username')
    const loginData = {
      username: credentials.username || credentials.email,
      password: credentials.password
    };
    return USE_MOCK_API ? mockAPI.auth.login(credentials) : api.post('/auth/login', loginData);
  },
  logout: () => USE_MOCK_API ? mockAPI.auth.logout() : api.post('/auth/logout'),
  verifyToken: () => USE_MOCK_API ? mockAPI.auth.verifyToken() : api.get('/auth/verify'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Users API
export const usersAPI = {
  getAll: (params) => USE_MOCK_API ? mockAPI.users.getAll(params) : api.get('/users', { params }),
  getById: (id) => USE_MOCK_API ? mockAPI.users.getById(id) : api.get(`/users/${id}`),
  create: (data) => USE_MOCK_API ? mockAPI.users.create(data) : api.post('/users', data),
  update: (id, data) => USE_MOCK_API ? mockAPI.users.update(id, data) : api.put(`/users/${id}`, data),
  delete: (id) => USE_MOCK_API ? mockAPI.users.delete(id) : api.delete(`/users/${id}`),
  updateProfile: (data) => {
    if (data instanceof FormData && !data.has('_method')) {
      data.append('_method', 'PUT');
      return api.post('/users/profile', data);
    }
    return api.put('/users/profile', data);
  },
};

// Members API
export const membersAPI = {
  getAll: (params = {}) => USE_MOCK_API
    ? mockAPI.members.getAll(params)
    : api.get('/members', { params }),
  getMembers: (params = {}) => api.get('/members', { params }),
  getMember: (id) => api.get(`/members/${id}`),
  getById: (id) => USE_MOCK_API ? mockAPI.members.getById(id) : api.get(`/members/${id}`),
  create: (data) => USE_MOCK_API
    ? mockAPI.members.create(data)
    : api.post('/members', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
  // For updates we use POST + _method=PUT so Laravel handles multipart form-data correctly
  update: (id, data) => {
    if (USE_MOCK_API) return mockAPI.members.update(id, data);

    // Ensure we have FormData so we can append _method
    let formData = data;
    if (data instanceof FormData) {
      formData = data;
    } else {
      formData = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    if (!formData.has('_method')) {
      formData.append('_method', 'PUT');
    }

    return api.post(`/members/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateMember: (id, data) =>
    api.post(`/members/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  delete: (id) => USE_MOCK_API ? mockAPI.members.delete(id) : api.delete(`/members/${id}`),
  approve: (id, data) => USE_MOCK_API ? mockAPI.members.approve(id, data) : api.post(`/members/${id}/approve`, data),
  reject: (id, data) => USE_MOCK_API ? mockAPI.members.reject(id, data) : api.post(`/members/${id}/reject`, data),
  requestResubmission: (id, data) => api.post(`/members/${id}/resubmit`, data),
  setStatus: (id, data) => api.post(`/members/${id}/status`, data),
  uploadDocument: (id, formData) => api.post(`/members/${id}/documents`, formData),
  getStats: () => USE_MOCK_API ? mockAPI.members.getStats() : api.get('/members/stats'),
  getRegions: () => api.get('/regions'),
  getMemberCategories: () => api.get('/member-categories'),
  lookupByMembershipId: (membershipId) => api.get('/members/lookup', {
    params: { membership_id: membershipId }
  }),
};

// Training Programs API
export const trainingAPI = {
  getAll: (params) => USE_MOCK_API ? mockAPI.training.getAll(params) : api.get('/training', { params }),
  getById: (id) => USE_MOCK_API ? mockAPI.training.getById(id) : api.get(`/training/${id}`),
  create: (data) => USE_MOCK_API ? mockAPI.training.create(data) : api.post('/training', data),
  update: (id, data) => USE_MOCK_API ? mockAPI.training.update(id, data) : api.put(`/training/${id}`, data),
  delete: (id) => USE_MOCK_API ? mockAPI.training.delete(id) : api.delete(`/training/${id}`),
  enrollMember: (trainingId, memberId) => api.post(`/training/${trainingId}/enroll/${memberId}`),
  getEnrollments: (id) => api.get(`/training/${id}/enrollments`),
  register: (trainingId, data) => api.post(`/training/${trainingId}/register`, data),
  getRegistrations: (trainingId, params) => api.get(`/training/${trainingId}/registrations`, { params }),
  updateRegistrationStatus: (registrationId, data) => api.post(`/training/registrations/${registrationId}/status`, data),
  downloadCertificate: (registrationId) =>
    apiFileDownload.get(`/training/registrations/${registrationId}/certificate`, { responseType: 'blob' }),
  regenerateCertificate: (registrationId) =>
    api.post(`/training/registrations/${registrationId}/certificate/regenerate`),
};

export const certificateSettingsAPI = {
  get: () => api.get('/certificate-settings'),
  update: (data) => api.put('/certificate-settings', data),
  uploadLogo: (formData) =>
    api.post('/certificate-settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadSignature: (formData) =>
    api.post('/certificate-settings/signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Events API
export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  registerMember: (eventId, memberId) => api.post(`/events/${eventId}/register/${memberId}`),
  getRegistrations: (id) => api.get(`/events/${id}/registrations`),
};

// Forms API
export const formsAPI = {
  getAll: (params) => api.get('/forms', { params }),
  getById: (id) => api.get(`/forms/${id}`),
  create: (data) => api.post('/forms', data),
  update: (id, data) => api.put(`/forms/${id}`, data),
  delete: (id) => api.delete(`/forms/${id}`),
  submit: (formId, data) => api.post(`/forms/${formId}/submit`, data),
  getSubmissions: (id) => api.get(`/forms/${id}/submissions`),
};

// Reports API
export const reportsAPI = {
  getDashboard: () => USE_MOCK_API ? mockAPI.dashboard.getStats() : api.get('/reports/dashboard'),
  getMembersReport: (params) => api.get('/reports/members', { params }),
  getTrainingReport: (params) => api.get('/reports/training', { params }),
  getEventsReport: (params) => api.get('/reports/events', { params }),
  exportReport: (type, params) => api.get(`/reports/export/${type}`, { 
    params, 
    responseType: 'blob' 
  }),
  exportMembers: (format, params = {}) => {
    // Use file download instance with longer timeout for PDF/Excel exports
    const client = format === 'pdf' ? apiFileDownload : apiFileDownload;
    return client.get('/reports/export/members', {
      params: { ...params, format },
      responseType: 'blob',
      timeout: format === 'pdf' ? 180000 : 120000, // 3 minutes for PDF, 2 minutes for Excel
    });
  },
};

// Regions API
export const regionsAPI = {
  getAll: () => USE_MOCK_API ? mockAPI.regions.getAll() : api.get('/regions'),
  getById: (id) => api.get(`/regions/${id}`),
};

// Member Categories API
export const memberCategoriesAPI = {
  getAll: () => USE_MOCK_API ? mockAPI.memberCategories.getAll() : api.get('/member-categories'),
  getById: (id) => api.get(`/member-categories/${id}`),
};

// Chat API
export const chatAPI = {
  searchUsers: (query) => api.get('/chat/users', { params: { query } }),
  listConversations: (params) => api.get('/chat/conversations', { params }),
  createConversation: (data) => api.post('/chat/conversations', data),
  getConversation: (id) => api.get(`/chat/conversations/${id}`),
  updateConversation: (id, data) => api.put(`/chat/conversations/${id}`, data),
  listMessages: (conversationId, params) =>
    api.get(`/chat/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data, attachments) => {
    if (attachments?.length) {
      const formData = new FormData();
      formData.append('message_type', data.message_type);
      formData.append('encrypted_payload', data.encrypted_payload);
      if (data.encrypted_metadata) {
        formData.append('encrypted_metadata', JSON.stringify(data.encrypted_metadata));
      }
      attachments.forEach((file) => formData.append('attachments[]', file));
      return api.post(`/chat/conversations/${conversationId}/messages`, formData);
    }
    return api.post(`/chat/conversations/${conversationId}/messages`, data);
  },
  uploadKeyPair: (data) => api.post('/chat/keys', data),
  getKeyPair: () => api.get('/chat/keys'),
  registerPushSubscription: (data) => api.post('/chat/push-subscriptions', data),
  unregisterPushSubscription: (endpoint) => api.delete('/chat/push-subscriptions', { data: { endpoint } }),
  downloadAttachment: (attachmentId) =>
    api.get(`/chat/attachments/${attachmentId}/download`, { responseType: 'blob' }),
};

// System Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
};

export const emailSettingsAPI = {
  getSettings: () => api.get('/email-settings'),
  updateSettings: (data) => api.put('/email-settings', data),
  sendTestEmail: (data) => api.post('/email-settings/test', data),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => USE_MOCK_API ? mockAPI.notifications.getAll(params) : api.get('/notifications', { params }),
  markAsRead: (id) => USE_MOCK_API ? mockAPI.notifications.markAsRead(id) : api.put(`/notifications/${id}/read`),
  markAllAsRead: () => USE_MOCK_API ? mockAPI.notifications.markAllAsRead() : api.put('/notifications/read-all'),
};

// Audit Logs API
export const auditAPI = {
  getAll: (params) => api.get('/audit', { params }),
  getById: (id) => api.get(`/audit/${id}`),
};

// News API
export const newsAPI = {
  getAll: (params) => api.get('/news', { params }),
  getById: (id) => api.get(`/news/${id}`),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`),
  uploadImage: (newsId, formData) => api.post(`/news/${newsId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getFeatured: () => api.get('/news/featured'),
  getRecent: (limit = 10) => api.get('/news/recent', { params: { limit } }),
};

// Publications API
export const publicationsAPI = {
  getAll: (params) => api.get('/publications', { params }),
  getById: (id) => api.get(`/publications/${id}`),
  create: (data) => api.post('/publications', data),
  update: (id, data) => api.put(`/publications/${id}`, data),
  delete: (id) => api.delete(`/publications/${id}`),
  uploadFile: (id, formData) => api.post(`/publications/${id}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCover: (id, formData) => api.post(`/publications/${id}/cover`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/publications/${id}/download`, { responseType: 'blob' }),
  preview: (id) => `${API_BASE_URL}/publications/${id}/preview`, // Returns URL for iframe/embed
  getFeatured: () => api.get('/publications/featured'),
  getRecent: (limit = 10) => api.get('/publications/recent', { params: { limit } }),
  byType: (type, params) => api.get(`/publications/type/${type}`, { params }),
};

// About Us API
export const aboutAPI = {
  get: () => axios.get(`${API_BASE_URL}/public/about`),
  update: (data) => api.put('/about', data),
  uploadImage: (formData) => api.post('/about/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: () => api.delete('/about/image'),
  uploadSliderImage: (formData) => api.post('/about/slider-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadBoardMemberPhoto: (formData) => api.post('/about/board-member-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Gallery API
export const galleryAPI = {
  // Albums
  getAlbums: (params) => api.get('/gallery/albums', { params }),
  getAlbum: (id) => api.get(`/gallery/albums/${id}`),
  createAlbum: (data) => api.post('/gallery/albums', data),
  updateAlbum: (id, data) => api.put(`/gallery/albums/${id}`, data),
  deleteAlbum: (id) => api.delete(`/gallery/albums/${id}`),
  uploadCoverImage: (id, formData) => api.post(`/gallery/albums/${id}/cover`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getFeatured: () => api.get('/gallery/featured'),
  getRecent: (limit = 6) => api.get('/gallery/recent', { params: { limit } }),
  
  // Media
  getMedia: (albumId) => api.get(`/gallery/albums/${albumId}/media`),
  addMedia: (albumId, data) => api.post(`/gallery/albums/${albumId}/media`, data),
  uploadImage: (albumId, formData) => api.post(`/gallery/albums/${albumId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMultipleImages: (albumId, formData) => api.post(`/gallery/albums/${albumId}/upload-multiple`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateMedia: (mediaId, data) => api.put(`/gallery/media/${mediaId}`, data),
  deleteMedia: (mediaId) => api.delete(`/gallery/media/${mediaId}`),
};

// Contact API
export const contactAPI = {
  // Contact Settings
  getSettings: () => api.get('/contact/settings'),
  updateSettings: (data) => api.put('/contact/settings', data),
  
  // Contact Messages
  submitMessage: (data) => api.post('/contact/message', data),
  getMessages: (params) => api.get('/contact/messages', { params }),
  getMessage: (id) => api.get(`/contact/messages/${id}`),
  updateMessage: (id, data) => api.put(`/contact/messages/${id}`, data),
  deleteMessage: (id) => api.delete(`/contact/messages/${id}`),
  getStats: () => api.get('/contact/messages/stats'),
};

// Website Settings API
export const websiteSettingsAPI = {
  getSettings: () => api.get('/website/settings'),
  updateSettings: (data) => api.put('/website/settings', data),
  uploadLogo: (formData) => api.post('/website/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/website/logo'),
  uploadFavicon: (formData) => api.post('/website/favicon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteFavicon: () => api.delete('/website/favicon'),
  uploadOgImage: (formData) => api.post('/website/og-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadLoginBackground: (formData) => api.post('/website/login-backgrounds', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLoginBackground: (data) => api.post('/website/login-backgrounds/delete', data),
};

// Survey API
export const surveyAPI = {
  // Public routes
  getAvailable: () => api.get('/surveys/available'),
  getBySlug: (slug) => api.get(`/surveys/slug/${slug}`),
  submitSurvey: (surveyId, data) => api.post(`/surveys/${surveyId}/submit`, data),
  
  // Admin routes (require authentication)
  getAll: (params) => api.get('/surveys', { params }),
  getById: (id) => api.get(`/surveys/${id}`),
  create: (data) => api.post('/surveys', data),
  update: (id, data) => api.put(`/surveys/${id}`, data),
  delete: (id) => api.delete(`/surveys/${id}`),
  toggleActive: (id) => api.post(`/surveys/${id}/toggle-active`),
  
  // Question management
  addQuestion: (surveyId, data) => api.post(`/surveys/${surveyId}/questions`, data),
  updateQuestion: (questionId, data) => api.put(`/surveys/questions/${questionId}`, data),
  deleteQuestion: (questionId) => api.delete(`/surveys/questions/${questionId}`),
  reorderQuestions: (surveyId, questionIds) => api.post(`/surveys/${surveyId}/reorder-questions`, { question_ids: questionIds }),
  
  // Responses & Statistics
  getResponses: (surveyId, params) => api.get(`/surveys/${surveyId}/responses`, { params }),
  getStatistics: (surveyId) => api.get(`/surveys/${surveyId}/statistics`),
};

// Jobs API
export const jobsAPI = {
  getAll: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  getApplications: (jobId, params) => api.get(`/jobs/${jobId}/applications`, { params }),
  apply: (jobId, data) => {
    // Expecting data as FormData for file upload
    return api.post(`/jobs/${jobId}/apply`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateApplicationStatus: (applicationId, data) => api.put(`/applications/${applicationId}/status`, data),
  updateApplicationRating: (applicationId, data) => api.put(`/applications/${applicationId}/rating`, data),
  getApplicationHistory: (applicationId) => api.get(`/applications/${applicationId}/history`),
  getStatistics: (params) => api.get('/applications/statistics', { params }),
};

export const membershipApplicationAPI = {
  // Public routes
  getPublicForms: () => api.get('/membership-applications/forms'),
  getFormBySlug: (slug) => api.get(`/membership-applications/forms/${slug}`),
  submitApplication: (data) => {
    const formData = new FormData();
    const fileFields = [];
    
    Object.keys(data).forEach(key => {
      const value = data[key];
      
      if (value instanceof File) {
        formData.append(key, value);
        fileFields.push(key);
      } else if (Array.isArray(value)) {
        // For arrays (like checkboxes), send as JSON string
        formData.append(key, JSON.stringify(value));
      } else if (value !== null && value !== undefined && value !== '') {
        // For regular values, send as-is (but skip empty strings for non-file fields that might be optional)
        formData.append(key, value);
      }
      // Skip null, undefined, and empty strings - they will be handled by validation
    });
    
    // Log what we're sending (for debugging)
    console.log('FormData being sent:', {
      keys: Array.from(formData.keys()),
      fileFields: fileFields,
      form_id: data.form_id,
      hasFiles: fileFields.length > 0,
    });
    
    // Don't set Content-Type header - browser/axios will set it automatically with boundary for FormData
    return api.post('/membership-applications/submit', formData);
  },
  
  // Admin routes
  getForms: (params) => api.get('/membership-application-forms', { params }),
  getForm: (id) => api.get(`/membership-application-forms/${id}`),
  createForm: (data) => api.post('/membership-application-forms', data),
  updateForm: (id, data) => api.put(`/membership-application-forms/${id}`, data),
  deleteForm: (id) => api.delete(`/membership-application-forms/${id}`),
  toggleActive: (id) => api.put(`/membership-application-forms/${id}/toggle-active`),
  
  // Submissions
  getSubmissions: (params) => api.get('/membership-application-submissions', { params }),
  getSubmission: (id) => api.get(`/membership-application-submissions/${id}`),
  updateSubmissionStatus: (id, data) => api.put(`/membership-application-submissions/${id}/status`, data),
  approveSubmission: (id) => api.post(`/membership-application-submissions/${id}/approve`),
  rejectSubmission: (id, reason) => api.post(`/membership-application-submissions/${id}/reject`, { rejection_reason: reason }),
  // Export functionality for membership applications has been disabled.
  // exportSubmissions: (format, params = {}) => api.get('/membership-application-submissions/export', {
  //   params: { ...params, format },
  //   responseType: 'blob'
  // }),
  
  // CSV Import
  previewImport: (formData) => api.post('/membership-applications/import/preview', formData),
  importApplications: (formData) => api.post('/membership-applications/import', formData),
  checkStatus: (params) => api.get('/application/status', { params }),
  findRegistration: (data) => api.post('/application/find-registration', data),
  getRegistration: (params) => api.get('/application/registration', { params }),
  submitRegistration: (data) => api.post('/application/registration', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const permissionsAPI = {
  list: () => api.get('/permissions'),
};

export const rolesAPI = {
  list: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key] instanceof File) {
        formData.append('image', data[key]);
      } else if (key === 'is_active') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.post('/testimonials', formData);
  },
  update: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key] instanceof File) {
        formData.append('image', data[key]);
      } else if (key === 'is_active') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/testimonials/${id}`, formData);
  },
  delete: (id) => api.delete(`/testimonials/${id}`),
};

// Partners API
export const partnersAPI = {
  getAll: (params) => api.get('/partners', { params }),
  getById: (id) => api.get(`/partners/${id}`),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'logo' && data[key] instanceof File) {
        formData.append('logo', data[key]);
      } else if (key === 'is_active') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.post('/partners', formData);
  },
  update: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'logo' && data[key] instanceof File) {
        formData.append('logo', data[key]);
      } else if (key === 'is_active') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/partners/${id}`, formData);
  },
  delete: (id) => api.delete(`/partners/${id}`),
};

// Donation Projects API
export const donationProjectsAPI = {
  getAll: (params) => api.get('/donation-projects', { params }),
  getById: (id) => api.get(`/donation-projects/${id}`),
  getFeatured: () => api.get('/donation-projects', { params: { featured: true } }),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'project_image' && data[key] instanceof File) {
        formData.append('project_image', data[key]);
      } else if (key === 'bank_accounts' && Array.isArray(data[key]) && data[key].length > 0) {
        // Send each bank account as separate fields only if array is not empty
        data[key].forEach((account, index) => {
          if (account && typeof account === 'object') {
            Object.keys(account).forEach(accKey => {
              const value = account[accKey];
              if (value !== null && value !== undefined && value !== '') {
                formData.append(`bank_accounts[${index}][${accKey}]`, value);
              }
            });
          }
        });
      } else if (key === 'is_active' || key === 'is_featured') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (key !== 'bank_accounts' && data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.post('/donation-projects', formData);
  },
  update: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'project_image' && data[key] instanceof File) {
        formData.append('project_image', data[key]);
      } else if (key === 'bank_accounts' && Array.isArray(data[key]) && data[key].length > 0) {
        // Send each bank account as separate fields only if array is not empty
        data[key].forEach((account, index) => {
          if (account && typeof account === 'object') {
            Object.keys(account).forEach(accKey => {
              const value = account[accKey];
              if (value !== null && value !== undefined && value !== '') {
                formData.append(`bank_accounts[${index}][${accKey}]`, value);
              }
            });
          }
        });
      } else if (key === 'is_active' || key === 'is_featured') {
        formData.append(key, data[key] ? '1' : '0');
      } else if (key !== 'bank_accounts' && data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/donation-projects/${id}`, formData);
  },
  delete: (id) => api.delete(`/donation-projects/${id}`),
};

// Donation Bank Accounts API (network-level accounts)
export const donationBankAccountsAPI = {
  getAll: (params) => api.get('/donation-bank-accounts', { params }),
  getById: (id) => api.get(`/donation-bank-accounts/${id}`),
  create: (data) => api.post('/donation-bank-accounts', data),
  update: (id, data) => api.put(`/donation-bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/donation-bank-accounts/${id}`),
};

export default api;
